import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => (n == null ? '0' : Number(n).toLocaleString('en-IN'));

function SegmentCard({ seg, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`segment-card-${seg.channel}`}
      className={`text-left p-5 rounded-xl border-2 transition-all ${
        active
          ? 'border-orange-500 bg-orange-50 shadow-md scale-[1.02]'
          : 'border-slate-200 bg-white hover:border-orange-300'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <span className="text-3xl">{seg.icon}</span>
        <span
          className="text-xs font-bold px-2 py-1 rounded-full"
          style={{ backgroundColor: `${seg.color}22`, color: seg.color }}
        >
          {seg.label}
        </span>
      </div>
      <p className="text-2xl font-bold text-slate-900">{fmtNum(seg.count)}</p>
      {seg.count_email_only != null && (
        <p className="text-xs text-slate-500 mt-1">
          ({fmtNum(seg.count_email_only)} email-only)
        </p>
      )}
      <p className="text-xs text-slate-600 mt-2 leading-snug">{seg.description}</p>
    </button>
  );
}

function AudienceTable({ token, channel }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stateFilter, setStateFilter] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [minStudents, setMinStudents] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [sending, setSending] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (stateFilter) params.set('state', stateFilter);
      if (districtFilter) params.set('district', districtFilter);
      if (minStudents) params.set('min_students', minStudents);
      const r = await fetch(`${API}/api/outreach-v2/audience/${channel}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      setItems(j.items || []);
      setTotal(j.total || 0);
      setSelectedIds([]);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token, channel, stateFilter, districtFilter, minStudents]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => setSelectedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAll = () => setSelectedIds((p) => (p.length === items.length ? [] : items.map((s) => s.id)));

  const sendSelected = async (dry) => {
    if (selectedIds.length === 0) { toast.error('पहले schools select करें'); return; }
    if (!dry && !window.confirm(`⚠️ ${selectedIds.length} चुने हुए schools को real WhatsApp message भेजें?`)) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/platform-admin/outreach/send-selected`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ school_ids: selectedIds, template_name: 'shiksha_mitra_intro', language: 'hi', dry_run: dry }),
      });
      const j = await r.json();
      if (!r.ok) { toast.error(j.detail || 'Failed'); }
      else {
        toast.success(`${dry ? 'Dry-run' : 'Sent'}: ${j.sent} · Failed: ${j.failed}`);
        if (!dry) load();
      }
    } catch { toast.error('Network error'); }
    finally { setSending(false); }
  };

  const exportReport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format, status: 'all' });
      if (stateFilter) params.set('state', stateFilter);
      if (districtFilter) params.set('district', districtFilter);
      const r = await fetch(`${API}/api/platform-admin/outreach/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { toast.error('Export failed'); setExporting(false); return; }
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `outreach_report.${format === 'pdf' ? 'pdf' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export error'); }
    finally { setExporting(false); }
  };

  const channelLabel = {
    whatsapp: '📱 WhatsApp Ready',
    email: '✉️ Email Ready',
    call: '📞 Call Only',
    postal: '📬 Postal Only',
  }[channel];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`audience-table-${channel}`}>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h3 className="font-bold text-slate-800">{channelLabel}</h3>
        <span className="text-sm text-slate-500">कुल {fmtNum(total)} schools</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <input
            placeholder="State (e.g. MADHYA PRADESH)"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value.toUpperCase())}
            className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg w-44"
            data-testid="audience-state-filter"
          />
          <input
            placeholder="District (e.g. INDORE)"
            value={districtFilter}
            onChange={(e) => setDistrictFilter(e.target.value.toUpperCase())}
            className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg w-36"
            data-testid="audience-district-filter"
          />
          <input
            placeholder="Min students"
            type="number"
            value={minStudents}
            onChange={(e) => setMinStudents(e.target.value)}
            className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg w-28"
            data-testid="audience-min-students-filter"
          />
          <button onClick={load} className="px-3 py-1.5 text-xs bg-slate-100 rounded-lg hover:bg-slate-200">🔄</button>
          <button onClick={() => exportReport('csv')} disabled={exporting} className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50" data-testid="audience-export-csv">⬇️ CSV</button>
          <button onClick={() => exportReport('pdf')} disabled={exporting} className="px-3 py-1.5 text-xs bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50" data-testid="audience-export-pdf">⬇️ PDF</button>
        </div>
      </div>

      {/* Selection toolbar (WhatsApp only) */}
      {channel === 'whatsapp' && selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="v2-selection-toolbar">
          <span className="text-sm font-semibold text-emerald-800">✓ {selectedIds.length} selected</span>
          <div className="ml-auto flex gap-2">
            <button onClick={() => sendSelected(true)} disabled={sending} className="px-3 py-1.5 bg-yellow-500 text-white rounded-full text-xs font-bold hover:bg-yellow-600 disabled:opacity-50" data-testid="v2-send-selected-dry">🧪 Dry Run</button>
            <button onClick={() => sendSelected(false)} disabled={sending} className="px-3 py-1.5 bg-emerald-600 text-white rounded-full text-xs font-bold hover:bg-emerald-700 disabled:opacity-50" data-testid="v2-send-selected-live">{sending ? '⏳...' : `🚀 चुने हुए को भेजें (${selectedIds.length})`}</button>
            <button onClick={() => setSelectedIds([])} className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded-full text-xs">Clear</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-slate-400">लोड हो रहा है...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-slate-400">कोई school नहीं मिला</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b">
                {channel === 'whatsapp' && (
                  <th className="py-2 px-2">
                    <input type="checkbox" checked={selectedIds.length === items.length && items.length > 0} onChange={toggleAll} data-testid="v2-check-all" />
                  </th>
                )}
                <th className="py-2 px-2">School</th>
                <th className="py-2 px-2">State / District</th>
                <th className="py-2 px-2">Students</th>
                <th className="py-2 px-2">Contact</th>
                <th className="py-2 px-2">Sent?</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s, i) => (
                <tr key={s.id || i} className={`border-b border-slate-100 hover:bg-slate-50 ${selectedIds.includes(s.id) ? 'bg-emerald-50/50' : ''}`}>
                  {channel === 'whatsapp' && (
                    <td className="py-2 px-2">
                      <input type="checkbox" checked={selectedIds.includes(s.id)} onChange={() => toggleSelect(s.id)} data-testid={`v2-check-${s.id}`} />
                    </td>
                  )}
                  <td className="py-2 px-2 font-medium text-slate-700 truncate max-w-xs">{s.school_name}</td>
                  <td className="py-2 px-2 text-xs text-slate-500">{s.state} / {s.district}</td>
                  <td className="py-2 px-2 text-xs">{s.total_students || '-'}</td>
                  <td className="py-2 px-2 text-xs font-mono">
                    {channel === 'whatsapp' && s.whatsapp_number}
                    {channel === 'email' && s.email}
                    {channel === 'call' && s.phone}
                    {channel === 'postal' && '—'}
                  </td>
                  <td className="py-2 px-2 text-xs">
                    {(channel === 'whatsapp' && s.outreach_sent) || (channel === 'email' && s.email_outreach_sent) ? (
                      <span className="text-emerald-600 font-semibold">✓ Sent</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EmailBatchSender({ token }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('founding_50');
  const [stateFilter, setStateFilter] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [minStudents, setMinStudents] = useState('');
  const [limit, setLimit] = useState(10);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/outreach-v2/email/templates`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((j) => setTemplates(j.templates || []))
      .catch(console.error);
    refreshStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const refreshStats = async () => {
    try {
      const r = await fetch(`${API}/api/outreach-v2/email/stats`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      setStats(j);
    } catch (e) { console.error(e); }
  };

  const runBatch = async (dry) => {
    setSending(true);
    setSendResult(null);
    if (dry) setDryRunResult(null);
    try {
      const r = await fetch(`${API}/api/outreach-v2/email/send-batch`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_key: selectedTemplate,
          state: stateFilter || null,
          district: districtFilter || null,
          min_students: minStudents ? parseInt(minStudents, 10) : null,
          limit: parseInt(limit, 10) || 10,
          dry_run: dry,
        }),
      });
      const j = await r.json();
      if (!r.ok) {
        toast.error(j.detail || 'Batch failed');
        return;
      }
      if (dry) {
        setDryRunResult(j);
        toast.success(`Dry-run: ${j.would_send} emails would be sent (${j.total_eligible} eligible)`);
      } else {
        setSendResult(j);
        toast.success(`Sent: ${j.sent} · Failed: ${j.failed} · Skipped: ${j.skipped}`);
        refreshStats();
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="email-batch-sender">
      <h3 className="font-bold text-slate-800 mb-4">✉️ Email Bulk Outreach</h3>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
          <div className="bg-emerald-50 rounded-lg p-3"><p className="text-xs text-slate-500">Sent</p><p className="text-xl font-bold text-emerald-700">{stats.succeeded}</p></div>
          <div className="bg-blue-50 rounded-lg p-3"><p className="text-xs text-slate-500">Opened</p><p className="text-xl font-bold text-blue-700">{stats.opened} <span className="text-xs">({stats.open_rate_pct}%)</span></p></div>
          <div className="bg-violet-50 rounded-lg p-3"><p className="text-xs text-slate-500">Clicked</p><p className="text-xl font-bold text-violet-700">{stats.clicked} <span className="text-xs">({stats.click_rate_pct}%)</span></p></div>
          <div className="bg-rose-50 rounded-lg p-3"><p className="text-xs text-slate-500">Failed</p><p className="text-xl font-bold text-rose-700">{stats.failed}</p></div>
          <div className="bg-slate-100 rounded-lg p-3"><p className="text-xs text-slate-500">Unsubscribed</p><p className="text-xl font-bold text-slate-700">{stats.unsubscribed}</p></div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Template</label>
          <select
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="email-template-select"
          >
            {templates.map((t) => (
              <option key={t.key} value={t.key}>{t.name_hi}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">State (optional)</label>
          <input
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value.toUpperCase())}
            placeholder="MADHYA PRADESH"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="email-state-filter"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">District (optional)</label>
          <input
            value={districtFilter}
            onChange={(e) => setDistrictFilter(e.target.value.toUpperCase())}
            placeholder="INDORE"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="email-district-filter"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Min Students</label>
            <input
              type="number"
              value={minStudents}
              onChange={(e) => setMinStudents(e.target.value)}
              placeholder="200"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="email-min-students"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Batch Size (max 100)</label>
            <input
              type="number"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              min="1" max="100"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="email-batch-limit"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-3 mt-5">
        <button
          onClick={() => runBatch(true)}
          disabled={sending}
          className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium disabled:opacity-50"
          data-testid="email-dry-run-btn"
        >
          🔍 Dry-Run Preview
        </button>
        <button
          onClick={() => {
            if (!window.confirm(`क्या आप ${limit} schools को real email भेजना चाहते हैं?`)) return;
            runBatch(false);
          }}
          disabled={sending || !dryRunResult}
          className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-bold disabled:opacity-50"
          data-testid="email-send-real-btn"
        >
          {sending ? '⏳ Sending...' : '🚀 Send Real Batch'}
        </button>
      </div>

      {dryRunResult && (
        <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="dry-run-preview">
          <p className="font-bold text-amber-900 mb-2">🔍 Dry-Run Preview</p>
          <p className="text-sm text-amber-800">कुल eligible: <strong>{dryRunResult.total_eligible}</strong> · इस batch में जाएँगे: <strong>{dryRunResult.would_send}</strong></p>
          <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
            {dryRunResult.preview?.map((p, i) => (
              <div key={i} className="text-xs bg-white rounded px-2 py-1 flex justify-between">
                <span className="truncate">{p.school_name}</span>
                <span className="text-slate-500 font-mono">{p.email}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {sendResult && (
        <div className="mt-5 p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="send-result">
          <p className="font-bold text-emerald-900 mb-2">✅ Batch Complete</p>
          <p className="text-sm">Sent: <strong>{sendResult.sent}</strong> · Failed: <strong>{sendResult.failed}</strong> · Skipped: <strong>{sendResult.skipped}</strong></p>
          <p className="text-xs text-emerald-700 mt-2">Stats refresh कर दिए हैं ऊपर। Open/Click rate 24-48 घंटों में update होंगे।</p>
        </div>
      )}
    </div>
  );
}

function WhatsAppBatchSender({ token }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('shiksha_mitra_intro');
  const [stateFilter, setStateFilter] = useState('MADHYA PRADESH');
  const [districtFilter, setDistrictFilter] = useState('INDORE');
  const [minStudents, setMinStudents] = useState('');
  const [sourceTag, setSourceTag] = useState('');  // empty = all sources
  const [limit, setLimit] = useState(10);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    // Load approved WA templates
    fetch(`${API}/api/whatsapp/templates`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((j) => {
        const approved = (j.templates || []).filter((t) => t.status === 'APPROVED');
        setTemplates(approved);
      })
      .catch(console.error);
  }, [token]);

  const runBatch = async (dry) => {
    setSending(true);
    setSendResult(null);
    if (dry) setDryRunResult(null);
    try {
      const r = await fetch(`${API}/api/platform-admin/outreach/send-batch`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          state: stateFilter || null,
          district: districtFilter || null,
          source_tag: sourceTag || null,
          min_students: minStudents ? parseInt(minStudents, 10) : null,
          template_name: selectedTemplate,
          language: 'hi',
          limit: parseInt(limit, 10) || 10,
          dry_run: dry,
          only_unsent: true,
        }),
      });
      const j = await r.json();
      if (!r.ok) {
        toast.error(j.detail || 'Batch failed');
        return;
      }
      if (dry) {
        setDryRunResult(j);
        toast.success(`Dry-run: ${j.sent || j.results?.length || 0} WA would be sent`);
      } else {
        setSendResult(j);
        toast.success(`Sent: ${j.sent || 0} · Failed: ${j.failed || 0}`);
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="whatsapp-batch-sender">
      <h3 className="font-bold text-slate-800 mb-1">📱 WhatsApp Bulk Outreach</h3>
      <p className="text-xs text-slate-500 mb-4">17,952 validated WA-ready schools — Meta approved templates के साथ batch भेजें</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">WA Template (Meta-approved)</label>
          <select
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="wa-template-select"
          >
            {templates.length === 0 ? (
              <option>shiksha_mitra_intro (default)</option>
            ) : (
              templates.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name} ({t.status})
                </option>
              ))
            )}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">State</label>
          <input
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value.toUpperCase())}
            placeholder="MADHYA PRADESH"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="wa-state-filter"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">District</label>
          <input
            value={districtFilter}
            onChange={(e) => setDistrictFilter(e.target.value.toUpperCase())}
            placeholder="INDORE"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="wa-district-filter"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Min Students (optional)</label>
            <input
              type="number"
              value={minStudents}
              onChange={(e) => setMinStudents(e.target.value)}
              placeholder="200"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="wa-min-students"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Batch Size (max 50)</label>
            <input
              type="number"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              min="1" max="50"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="wa-batch-limit"
            />
          </div>
        </div>
      </div>

      <div className="mt-3">
        <label className="text-xs font-medium text-slate-600 block mb-1">Source tag (blank = सभी sources)</label>
        <input
          value={sourceTag}
          onChange={(e) => setSourceTag(e.target.value)}
          placeholder="indore_private_schools_pdf_2026 (या blank छोड़ें)"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
          data-testid="wa-source-tag"
        />
      </div>

      <div className="flex gap-3 mt-5">
        <button
          onClick={() => runBatch(true)}
          disabled={sending}
          className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium disabled:opacity-50"
          data-testid="wa-dry-run-btn"
        >
          🔍 Dry-Run Preview
        </button>
        <button
          onClick={() => {
            if (!window.confirm(`क्या आप ${limit} schools को real WhatsApp message भेजना चाहते हैं?\n\nMeta को ₹0.40/message charge होगा.`)) return;
            runBatch(false);
          }}
          disabled={sending || !dryRunResult}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold disabled:opacity-50"
          data-testid="wa-send-real-btn"
        >
          {sending ? '⏳ Sending...' : '🚀 Send Real WhatsApp Batch'}
        </button>
      </div>

      {dryRunResult && (
        <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="wa-dry-run-preview">
          <p className="font-bold text-amber-900 mb-2">🔍 Dry-Run Preview ({dryRunResult.results?.length || 0} schools)</p>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {(dryRunResult.results || []).slice(0, 20).map((r, i) => (
              <div key={i} className="text-xs bg-white rounded px-2 py-1 flex justify-between">
                <span className="truncate">{r.school_name || r.school_id}</span>
                <span className="text-slate-500 font-mono">{r.phone || r.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {sendResult && (
        <div className="mt-5 p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="wa-send-result">
          <p className="font-bold text-emerald-900 mb-2">✅ WA Batch Complete</p>
          <p className="text-sm">Sent: <strong>{sendResult.sent || 0}</strong> · Failed: <strong>{sendResult.failed || 0}</strong></p>
          <p className="text-xs text-emerald-700 mt-2">Conversion Proof Panel में 24-48 घंटों में leads आना शुरू होंगी।</p>
        </div>
      )}
    </div>
  );
}

export default function OutreachV2Panel({ token }) {
  const [segments, setSegments] = useState([]);
  const [activeChannel, setActiveChannel] = useState('whatsapp');
  const [loading, setLoading] = useState(true);
  const [needsBackfill, setNeedsBackfill] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState(null);

  const loadSegments = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/outreach-v2/segments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      setSegments(j.segments || []);
      setNeedsBackfill(!!j.needs_backfill);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  const triggerBackfill = async () => {
    try {
      await fetch(`${API}/api/outreach-v2/admin/rebuild-channels`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Backfill शुरू हो गया — कुछ मिनट लगेंगे');
      pollBackfill();
    } catch (e) { toast.error('Failed to start backfill'); }
  };

  const pollBackfill = useCallback(async () => {
    const tick = async () => {
      try {
        const r = await fetch(`${API}/api/outreach-v2/admin/backfill-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const j = await r.json();
        setBackfillStatus(j);
        if (j.running) {
          setTimeout(tick, 5000);
        } else if (j.progress > 0) {
          // Finished — reload segments
          loadSegments();
        }
      } catch { /* */ }
    };
    tick();
  }, [token, loadSegments]);

  useEffect(() => { loadSegments(); }, [loadSegments]);
  // Auto-poll if backfill is currently running (e.g., admin refreshed mid-run)
  useEffect(() => {
    if (backfillStatus?.running) {
      const t = setTimeout(pollBackfill, 3000);
      return () => clearTimeout(t);
    }
  }, [backfillStatus, pollBackfill]);

  return (
    <div className="space-y-6" data-testid="outreach-v2-panel">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-1">🎯 Outreach v2 — Channel Router</h2>
        <p className="text-sm text-slate-500">4,49,674 schools — हर एक के लिए सही channel choose करें</p>
      </div>

      {/* First-time backfill prompt */}
      {(needsBackfill || backfillStatus?.running) && (
        <div
          className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5"
          data-testid="backfill-prompt"
        >
          {backfillStatus?.running ? (
            <>
              <h3 className="font-bold text-amber-900 mb-2">⚙️ Schools को classify किया जा रहा है...</h3>
              <p className="text-sm text-amber-800 mb-3">
                {(backfillStatus.progress || 0).toLocaleString('en-IN')} / {(backfillStatus.total || 0).toLocaleString('en-IN')} schools processed
                ({backfillStatus.progress_pct || 0}%)
              </p>
              <div className="h-3 bg-amber-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 transition-all duration-500"
                  style={{ width: `${backfillStatus.progress_pct || 0}%` }}
                />
              </div>
              <p className="text-xs text-amber-700 mt-2">कुछ मिनट लगेंगे (~10 min total). Page open रख सकते हैं।</p>
            </>
          ) : (
            <>
              <h3 className="font-bold text-amber-900 mb-2">⚙️ One-time Setup चाहिए</h3>
              <p className="text-sm text-amber-800 mb-3">
                Schools को channels में classify करने के लिए एक-बार setup चलाना है। ~10 मिनट का काम है, फिर सब instant load होगा।
              </p>
              <button
                onClick={triggerBackfill}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-bold"
                data-testid="trigger-backfill-btn"
              >
                🚀 अभी Setup शुरू करें
              </button>
            </>
          )}
        </div>
      )}

      {/* Segments */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-slate-100 animate-pulse rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {segments.map((s) => (
            <SegmentCard
              key={s.channel}
              seg={s}
              active={activeChannel === s.channel}
              onClick={() => setActiveChannel(s.channel)}
            />
          ))}
        </div>
      )}

      {/* Channel-specific actions */}
      {activeChannel === 'whatsapp' && <WhatsAppBatchSender token={token} />}
      {activeChannel === 'email' && <EmailBatchSender token={token} />}

      <AudienceTable token={token} channel={activeChannel} />
    </div>
  );
}
